import { useMemo, useState, useCallback, useRef } from 'react';
import { Upload, FileQuestion } from 'lucide-react';
import { useAppSelector } from '@/store/hooks';
import { selectSelectedItem, selectProjectTasks, selectUserRole } from '@/store/selectors';
import type { ProjectTask } from '@/types/task';
import { PreviewItemsList } from './PreviewItemsList';
import { PdfViewer } from './components/PdfViewer';
import { VideoPlayer } from './components/VideoPlayer';
import { AudioPlayer } from './components/AudioPlayer';
import { ImageViewer, type ImageNewTaskData } from './components/ImageViewer';
import { TaskPopover } from './components/TaskPopover';
import { NewTaskForm } from './components/NewTaskForm';
import type { NewTaskClickData } from './components/AnnotationLayer';
import { taskAnchor, type MediaLayout } from './lib/annotationGeometry';

/** Media types the work section can actually render. Anything else gets a fallback message. */
export const SUPPORTED_MEDIA_TYPES = ['video', 'audio', 'image', 'doc'] as const;

/** Resource item from the items store */
interface SelectedItem {
  id: string;
  type: string;
  parent: string;
  signedUrl?: string;
  thumbnailUrl?: string;
  mimeType?: string;
  [k: string]: unknown;
}

interface WorkSectionProps {
  messagesTabOpen: boolean;
  selectedTask: ProjectTask | null;
  updateSelectedTask: (task: ProjectTask | null) => void;
  updateModalOpen: (type?: string, params?: Record<string, unknown>) => void;
  match: { params: { id: string }; url: string };
}

export function WorkSection({
  messagesTabOpen,
  selectedTask,
  updateSelectedTask,
  updateModalOpen,
  match,
}: WorkSectionProps) {
  const selectedItem = useAppSelector(selectSelectedItem) as SelectedItem | null;
  const tasks = useAppSelector(selectProjectTasks) as ProjectTask[] | null;
  const userRole = useAppSelector(selectUserRole);
  const isViewer = userRole === 'viewer' || userRole === 'guest';

  // Video ref for snapshot capture
  const videoElRef = useRef<HTMLVideoElement | null>(null);

  // New task creation state per media type
  const [videoNewTask, setVideoNewTask] = useState<NewTaskClickData | null>(null);
  const [audioClickTime, setAudioClickTime] = useState<number | null>(null);
  const [imageNewTask, setImageNewTask] = useState<ImageNewTaskData | null>(null);

  // Where the picture sits in the player, reported by the video/image viewer, so the task popover
  // anchors at the task's spot on the picture whichever way the task was selected
  const [mediaLayout, setMediaLayout] = useState<MediaLayout | null>(null);
  const popoverAnchor = useMemo(() => {
    if (!selectedTask || !mediaLayout || (selectedItem?.type !== 'video' && selectedItem?.type !== 'image')) return undefined;
    return taskAnchor(selectedTask, mediaLayout, selectedItem.type) ?? undefined;
  }, [selectedTask, mediaLayout, selectedItem?.type]);

  const clearNewTask = useCallback(() => {
    setVideoNewTask(null);
    setAudioClickTime(null);
    setImageNewTask(null);
  }, []);

  const selectedItemTasks = useMemo(() => {
    if (!selectedItem || !tasks) return [];
    return tasks.filter((t) => t.resource === selectedItem.id);
  }, [tasks, selectedItem]);

  const highestIndex = useMemo(() => {
    if (!selectedItemTasks.length) return 1;
    return Math.max(...selectedItemTasks.map((t) => t.index), 0) + 1;
  }, [selectedItemTasks]);

  const tasksVisible = !messagesTabOpen;

  return (
    <div className="mr-4 flex-7 max-lg:mr-0">
      {selectedItem ? (
        <>
          <div className="flex h-140 w-full justify-center rounded-lg border border-border bg-background mb-4">
            {/* ── Video ─────────────────────────────────── */}
            {selectedItem.type === 'video' && (
              <div className="relative size-full">
                <VideoPlayer
                  resource={selectedItem}
                  tasks={selectedItemTasks}
                  selectedTask={selectedTask ?? undefined}
                  updateSelectedTask={updateSelectedTask}
                  messagesTabOpen={messagesTabOpen}
                  highestIndex={highestIndex}
                  userRole={userRole}
                  onCreateTask={setVideoNewTask}
                  videoRefCallback={(el) => { videoElRef.current = el; }}
                  onLayoutChange={setMediaLayout}
                />
                {selectedTask && tasksVisible && (
                  <TaskPopover
                    task={selectedTask}
                    onClose={() => updateSelectedTask(null)}
                    onUpdateTask={(t) => updateSelectedTask(t)}
                    mediaType="video"
                    anchor={popoverAnchor}
                  />
                )}
                {videoNewTask && (
                  <NewTaskForm
                    mediaType="video"
                    resource={selectedItem}
                    taskIndex={highestIndex}
                    onClose={clearNewTask}
                    coordX={videoNewTask.coordX}
                    coordY={videoNewTask.coordY}
                    time={videoNewTask.time}
                    geometry={videoNewTask.geometry}
                    pixelCoords={videoNewTask.pixelCoords}
                    initialTaskType={videoNewTask.taskType}
                    videoRef={videoElRef}
                    containerWidth={videoNewTask.containerWidth}
                    containerHeight={videoNewTask.containerHeight}
                  />
                )}
              </div>
            )}

            {/* ── Audio ─────────────────────────────────── */}
            {selectedItem.type === 'audio' && (
              <div className="relative size-full">
                <AudioPlayer
                  resource={selectedItem}
                  tasks={selectedItemTasks}
                  selectedTask={selectedTask ?? undefined}
                  updateSelectedTask={updateSelectedTask}
                  messagesTabOpen={messagesTabOpen}
                  userRole={userRole}
                  onCreateTask={(time) => setAudioClickTime(time)}
                />
                {selectedTask && tasksVisible && (
                  <TaskPopover
                    task={selectedTask}
                    onClose={() => updateSelectedTask(null)}
                    onUpdateTask={(t) => updateSelectedTask(t)}
                    mediaType="audio"
                  />
                )}
                {audioClickTime != null && (
                  <NewTaskForm
                    mediaType="audio"
                    resource={selectedItem}
                    taskIndex={highestIndex}
                    onClose={clearNewTask}
                    time={audioClickTime}
                  />
                )}
              </div>
            )}

            {/* ── Image ─────────────────────────────────── */}
            {selectedItem.type === 'image' && (
              <div className="relative h-full">
                <ImageViewer
                  resource={selectedItem}
                  tasks={selectedItemTasks}
                  selectedTask={selectedTask ?? undefined}
                  updateSelectedTask={updateSelectedTask}
                  messagesTabOpen={messagesTabOpen}
                  userRole={userRole}
                  onCreateTask={(data) => setImageNewTask(data)}
                  onLayoutChange={setMediaLayout}
                />
                {selectedTask && tasksVisible && (
                  <TaskPopover
                    task={selectedTask}
                    onClose={() => updateSelectedTask(null)}
                    onUpdateTask={(t) => updateSelectedTask(t)}
                    mediaType="image"
                    anchor={popoverAnchor}
                  />
                )}
                {imageNewTask && (
                  <NewTaskForm
                    mediaType="image"
                    resource={selectedItem}
                    taskIndex={highestIndex}
                    onClose={clearNewTask}
                    coordX={imageNewTask.percentX}
                    coordY={imageNewTask.percentY}
                    naturalCoord={imageNewTask.coord}
                    geometry={imageNewTask.geometry}
                    pixelCoords={imageNewTask.coords}
                    initialTaskType={imageNewTask.taskType}
                    containerWidth={imageNewTask.containerWidth}
                    containerHeight={imageNewTask.containerHeight}
                  />
                )}
              </div>
            )}

            {/* ── PDF ───────────────────────────────────── */}
            {selectedItem.type === 'doc' && (
              <PdfViewer resource={selectedItem} />
            )}

            {/* ── Fallback for unknown/unsupported types ──
                Without this, a selected item of any other type rendered an
                empty box (reported as "project opens with nothing"). */}
            {!SUPPORTED_MEDIA_TYPES.includes(selectedItem.type as typeof SUPPORTED_MEDIA_TYPES[number]) && (
              <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <FileQuestion className="size-10" />
                <p className="text-sm font-medium">This file type can't be previewed</p>
              </div>
            )}
          </div>
          <PreviewItemsList updateSelectedTask={updateSelectedTask} />
        </>
      ) : (
        <div className="flex h-140 w-full items-center justify-center rounded-lg border border-border bg-background">
          {!isViewer ? (
            <button
              type="button"
              onClick={() => updateModalOpen('fileUpload')}
              className="flex flex-col items-center gap-3 text-muted-foreground hover:text-accent-foreground transition-colors"
            >
              <Upload className="size-10" />
              <span className="text-sm font-medium">Add files</span>
            </button>
          ) : (
            <p className="text-center text-muted-foreground">No content</p>
          )}
        </div>
      )}
    </div>
  );
}
